import React, { useState } from "react";
import { Panel } from "../components/Panel";
import { usePlayer } from "../context/PlayerContext";
import { api, apiErr } from "../api/client";
import { Sound } from "../game/audio";
import { Volume2, Music, Gauge, LogOut, LogIn, Tv, BarChart3, User, Check } from "lucide-react";
import { toast } from "sonner";

export default function Settings({ onClose, onAuth }) {
  const { profile, applyUser, logout } = usePlayer();
  const s = profile.settings || {};
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState(profile.displayName || "");
  const [savingName, setSavingName] = useState(false);
  const stats = profile.statistics || {};
  const isGuest = profile.accountType === "guest";

  const saveName = async () => {
    const n = name.trim();
    if (n.length < 2 || n.length > 14) { toast.error("Name must be 2 to 14 characters"); return; }
    if (n === profile.displayName) return;
    setSavingName(true);
    try { const { data } = await api.changeName(n); applyUser(data.user); toast.success("Username updated!"); }
    catch (e) { toast.error(apiErr(e, "Could not update name.")); }
    finally { setSavingName(false); }
  };

  const set = async (patch) => {
    if (patch.sound !== undefined) Sound.setEnabled(patch.sound);
    if (patch.music !== undefined) Sound.setMusic(patch.music);
    try { await api.settings(patch); applyUser({ ...profile, settings: { ...s, ...patch } }); } catch (e) { console.warn("Failed to save settings", e); }
  };

  const removeAds = async () => {
    setBusy(true);
    try { const { data } = await api.checkout("remove_ads"); window.location.href = data.checkout_url; }
    catch (e) { alert(apiErr(e)); setBusy(false); }
  };

  const Toggle = ({ icon: Icon, label, value, onChange, testid }) => (
    <div className="flex items-center justify-between card p-3">
      <div className="flex items-center gap-2 text-slate-200"><Icon size={18} className="text-cyan-400" /> {label}</div>
      <button onClick={() => onChange(!value)} className={`w-12 h-6 rounded-full transition-all relative ${value ? "bg-amber-500" : "bg-slate-700"}`} data-testid={testid}>
        <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${value ? "left-6" : "left-0.5"}`} />
      </button>
    </div>
  );

  return (
    <Panel title="Settings" onClose={onClose}>
      <div className="card p-3 mb-3" data-testid="username-editor">
        <div className="flex items-center gap-2 text-slate-200 mb-2"><User size={18} className="text-cyan-400" /> Username</div>
        <div className="flex gap-2">
          <input
            value={name}
            onChange={(e) => setName(e.target.value.slice(0, 14))}
            onKeyDown={(e) => { if (e.key === "Enter") saveName(); }}
            maxLength={14}
            className="flex-1 bg-slate-900 border border-slate-700 rounded-xl px-3 py-2.5 text-white font-bold focus:border-amber-500 outline-none"
            data-testid="username-input"
            placeholder="Your name"
          />
          <button disabled={savingName || name.trim() === profile.displayName || name.trim().length < 2} onClick={saveName}
            className="btn-primary px-4 py-2.5 flex items-center gap-1 disabled:opacity-40" data-testid="username-save">
            <Check size={16} /> Save
          </button>
        </div>
      </div>
      <div className="space-y-2">
        <Toggle icon={Volume2} label="Sound Effects" value={s.sound !== false} onChange={(v) => set({ sound: v })} testid="toggle-sound" />
        <Toggle icon={Music} label="Music" value={s.music !== false} onChange={(v) => set({ music: v })} testid="toggle-music" />
        <div className="flex items-center justify-between card p-3">
          <div className="flex items-center gap-2 text-slate-200"><Gauge size={18} className="text-cyan-400" /> Quality</div>
          <div className="flex gap-1">
            {["low", "high"].map((q) => (
              <button key={q} onClick={() => set({ quality: q })} className={`px-3 py-1 rounded-lg text-xs font-bold uppercase ${((s.quality || "high") === q) ? "btn-primary" : "bg-slate-800 text-slate-400"}`} data-testid={`quality-${q}`}>{q}</button>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-4">
        <div className="text-xs uppercase tracking-widest text-amber-400 font-bold mb-2 flex items-center gap-1"><BarChart3 size={14} /> Career Stats</div>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <Stat label="Matches" value={stats.matchesPlayed || 0} />
          <Stat label="Wins" value={stats.wins || 0} />
          <Stat label="Objects Smashed" value={(stats.totalObjectsSmashed || 0).toLocaleString()} />
          <Stat label="Total KOs" value={stats.totalKills || 0} />
          <Stat label="Best Combo" value={`x${stats.bestCombo || 0}`} />
          <Stat label="Personal Best" value={(stats.personalBest || 0).toLocaleString()} />
        </div>
      </div>

      <div className="mt-4 space-y-2">
        {!profile.adsRemoved && (
          <button disabled={busy} onClick={removeAds} className="btn-ghost w-full py-2.5 flex items-center justify-center gap-2" data-testid="remove-ads-btn">
            <Tv size={16} /> Remove Ads (Cosmetic — no gameplay advantage)
          </button>
        )}
        {isGuest ? (
          <button onClick={onAuth} className="btn-primary w-full py-3 flex items-center justify-center gap-2" data-testid="connect-account-btn">
            <LogIn size={18} /> Save Progress / Connect Account
          </button>
        ) : (
          <button onClick={() => { logout(); onClose(); }} className="btn-ghost w-full py-2.5 flex items-center justify-center gap-2 border-red-500/30 text-red-300" data-testid="logout-btn">
            <LogOut size={16} /> Log Out
          </button>
        )}
      </div>
    </Panel>
  );
}
function Stat({ label, value }) {
  return (
    <div className="card p-2.5 flex flex-col">
      <span className="text-slate-400 text-[11px] uppercase">{label}</span>
      <span className="text-white font-black">{value}</span>
    </div>
  );
}
